import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { FormControlLabel, Switch } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1)
    }
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  hint: {
    opacity: 0.7,
    marginTop: theme.spacing(1)
  }
}));

const ForbiddenWordSchema = Yup.object().shape({
  term: Yup.string()
    .min(2, () => i18n.t("validation.tooShort"))
    .required(() => i18n.t("validation.required"))
});

const ForbiddenWordModal = ({ open, onClose, forbiddenWordId, reload }) => {
  const classes = useStyles();

  const initialState = {
    term: "",
    replacement: ""
  };

  const [forbiddenWord, setForbiddenWord] = useState(initialState);
  const [active, setActive] = useState(true);

  useEffect(() => {
    try {
      (async () => {
        if (!forbiddenWordId) {
          setForbiddenWord(initialState);
          setActive(true);
          return;
        }

        const { data } = await api.get(`/forbidden-words/${forbiddenWordId}`);
        setActive(!!data.active);
        setForbiddenWord(prevState => ({
          ...prevState,
          term: data.term || "",
          replacement: data.replacement || ""
        }));
      })();
    } catch (err) {
      toastError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forbiddenWordId, open]);

  const handleClose = () => {
    setForbiddenWord(initialState);
    setActive(true);
    onClose();
  };

  const handleSave = async values => {
    const payload = { ...values, active };
    try {
      if (forbiddenWordId) {
        await api.put(`/forbidden-words/${forbiddenWordId}`, payload);
      } else {
        await api.post("/forbidden-words", payload);
      }
      toast.success(i18n.t("forbiddenWords.modal.toasts.saved"));
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle id="form-dialog-title">
          {forbiddenWordId
            ? i18n.t("forbiddenWords.modal.title.edit")
            : i18n.t("forbiddenWords.modal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={forbiddenWord}
          enableReinitialize={true}
          validationSchema={ForbiddenWordSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSave(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("forbiddenWords.modal.form.term")}
                    name="term"
                    error={touched.term && Boolean(errors.term)}
                    helperText={touched.term && errors.term}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    autoFocus
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("forbiddenWords.modal.form.replacement")}
                    name="replacement"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={e => setActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={i18n.t("common.active")}
                />
                <Typography variant="caption" component="p" className={classes.hint}>
                  {i18n.t("forbiddenWords.modal.pluralHint")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {forbiddenWordId ? i18n.t("common.save") : i18n.t("common.add")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ForbiddenWordModal;
